import Browser from '@/core/types/browser';
import browser from '@/core/browser';
import TokenMgmt from '@/core/TokenMgmt';
import { TokenProps } from '@/core/types/token';
import EventEmitter, { EventType, Handler } from '@/core/EventEmitter';
import { loadScript } from '@/core/utils';
import WdzUploader from '@/core/Uploader';
import { ActionRes } from '../types/ovopark';
import { getLanguage, setLanguage, Language, getTimeZone } from '@/core/language';
import { setNavTitle, setNavVisible } from './nav-bar';
import http from '@/core/http-request';
import { getWatermark, addWatermark, removeWatermark, addGlobalWatermark } from '@/core/watermark';
import { buriedPointUpload, initBuriedPoint } from '@/core/buried-point';
import { getMyInfo } from '@/core/api/user';
import { gotoCostCenter } from '@/core/request';
import type { Composer } from 'vue-i18n';
import { wrapI18nTranslate, updateActiveReplaceRules, setEnterpriseType } from '@/core/i18n-term';

type I18nModule = string | [string, string] | [string, string[]];
type LocaleObject = Record<string, string>;

export default class Ovopark extends EventEmitter {
  /** browser对象 */
  public readonly browser: Browser;
  /** 文件上传对象 */
  public readonly uploader: WdzUploader;
  /** 是否 万店掌app */
  public readonly isApp: boolean;
  /** 设备标识 */
  public uuid?: string;
  /** 语言 */
  public language?: string;
  /** api语言类型 */
  public languageType?: string;
  /** 时区 */
  public timeZone?: string;
  /** 是否加载H5导航栏 */
  public isNavbar: boolean;

  /** token管理对象 */
  private tokenMgmt: TokenMgmt;
  /** window.top对象，兼容iframe */
  private win: Window;
  /** 当前用户信息 */
  private myInfo: any;
  /** 资源加载的域名，用来动态加载sdk和语言包 */
  private sourceOrigin: string;
  /** 备用语言 */
  private fallbackLanguage: Language;
  /** vue-i18n对象 */
  private i18n: Composer | null;

  constructor(options?: { sourceOrigin?: string; fallbackLanguage?: Language }) {
    super();
    this.sourceOrigin = options?.sourceOrigin || '';
    this.fallbackLanguage = options?.fallbackLanguage || Language.ZH_CN;
    this.languageType = '';
    this.win = window.top || window;
    this.isApp = browser.ovopark;
    this.browser = browser;
    this.language = getLanguage();
    this.timeZone = getTimeZone();
    this.uploader = new WdzUploader({ client: this.isApp ? 'webview' : 'h5' }); // app内嵌为webview
    this.tokenMgmt = new TokenMgmt(this.isApp);
    this.bindTokenEvents(); // 需要先绑定事件
    this.isApp && this.appCallMethods();
    this.i18n = null;

    // 中移千里眼App，加载导航栏
    this.isNavbar = browser.cmiot && browser.mobile;

    // 初始化埋点
    initBuriedPoint();
  }

  public async ready(
    cb: (data: { token: string; messages: LocaleObject; vantLocale: LocaleObject }) => void,
    ...args: Array<I18nModule>
  ) {
    // 加载第三方sdk
    if (browser.weixin) {
      await loadScript(
        `https://res.wx.qq.com/open/js/jweixin-${browser.wxwork ? '1.2.0' : '1.6.0'}.js`,
      );
    } else if (browser.dingding) {
      await loadScript('https://g.alicdn.com/dingding/dingtalk-jsapi/2.13.42/dingtalk.open.js');
    } else if (browser.feishu) {
      await loadScript('https://lf1-cdn-tos.bytegoofy.com/goofy/lark/op/h5-js-sdk-1.5.16.js');
    } else if (browser.cmiot) {
      await loadScript(`${this.sourceOrigin}/web-sdk/static/cmiot/cmiot.umd.js`);
    } else if (browser.weaver) {
      await loadScript(`${this.sourceOrigin}/web-sdk/static/weaver/jem.js`);
    }
    await this.initToken();

    const tempI18n = args[0] as unknown as Composer;
    if (tempI18n && typeof tempI18n.t === 'function') {
      // 第二个参数是i18n对象，从 args 中移除
      args.splice(0, 1);
      this.i18n = tempI18n;
    }

    let messages: LocaleObject;
    let vantLocale: LocaleObject;
    if (args.length) {
      const locales = await this.loadLocale(args);
      messages = locales[0];
      vantLocale = locales[1];
    } else {
      // 未传入模块信息，不加载语言包，返回两个空对象，防止业务项目注册语言出问题
      messages = {};
      vantLocale = {};
    }

    if (!this.isApp) {
      // 非app环境，根据后台配置添加全局水印
      try {
        const userInfo = await this.getMyInfo();
        addGlobalWatermark(userInfo.data);
      } catch (error) {
        console.error('🚀 ~ ovopark.ready ~ 获取用户信息失败，不添加全局水印', error);
      }
    }

    if (typeof cb === 'function') {
      await cb({ token: this.getToken(), messages, vantLocale });
    }
  }

  /**
   * 动态引入script
   * @param src script资源地址
   * @returns Promise
   */
  public loadScript = loadScript;

  /** 获取App方法 */
  public getAppFunction(name: string) {
    if (browser.android) {
      return this.win.webview && this.win.webview[name];
    } else if (browser.ios) {
      return (
        this.win.webkit && this.win.webkit.messageHandlers && this.win.webkit.messageHandlers[name]
      );
    } else if (browser.harmony) {
      return this.win.webview && this.win.webview[name];
    }
  }

  /**
   * js调用app统一方法
   * @param name app提供的方法名
   * @param params 参数
   * @returns Promise
   */
  public action<T>(name: string, params?: object): Promise<T> {
    console.log('🚀 ~ file: Ovopark.ts ~ action ~ name:', name);
    if (!this.isApp) {
      // 非万店掌app内嵌页面
      return Promise.reject({
        code: '10001',
        message: 'Page is not in ovopark app!',
      });
    }

    return new Promise<T>((resolve, reject) => {
      let data: any;
      try {
        data = params === undefined ? {} : { ...params };
      } catch (ex) {
        // 参数不是object对象
        return Promise.reject({
          code: '10003',
          message: 'Params is not an object!',
        });
      }

      // window上注册回调方法给app使用
      const callback = `app_cb_${~~(Math.random() * 10000)}_${Date.now()}`;
      data.callback = callback;
      this.win[callback] = (res: ActionRes) => {
        console.log('🚀 ~ file: Ovopark.ts ~ callback: ' + name, res);
        delete this.win[callback];
        // res为undefined 认为是调用成功
        if (res?.error) {
          reject(res);
        } else {
          resolve(res as T);
        }
      };

      // 根据设备类型，调用app提供的方法
      const fn = this.getAppFunction(name);
      if (!fn) {
        reject({
          code: '10002',
          message: `Function '${name}' is undefined.`,
        });
        return;
      }

      if (browser.android) {
        fn(JSON.stringify(data));
      } else if (browser.ios) {
        fn.postMessage(data);
      } else if (browser.harmony) {
        // 鸿蒙直接 fn() 调不到App方法...
        this.win.webview[name](data);
      }
    });
  }
  /**
   * js调用app兼容老方法
   * @param name app提供的方法名
   * @param params 参数
   */
  public actionNoCb(name: string, ...params: Array<any>): void {
    console.log('🚀 ~ file: Ovopark.ts ~ actionNoCb ~ name:', name);
    if (!this.isApp) {
      // 非万店掌app内嵌页面
      // throw {
      //   code: '10001',
      //   message: 'Page is not in ovopark app!',
      // };
    }

    // 根据设备类型，调用app提供的方法
    const fn = this.getAppFunction(name);
    if (!fn) {
      console.log(`!!! Function '${name}' is undefined.`);
      return;
    }

    if (browser.android) {
      fn(...params);
    } else if (browser.ios) {
      fn.postMessage(...params);
    } else if (browser.harmony) {
      // 鸿蒙直接 fn() 调不到App方法...
      this.win.webview[name](...params);
    }
  }

  /**
   * 设置页面标题
   * @param title 标题
   */
  public setTitle(title: string) {
    if (this.isApp) {
      this.action('setTitle', { title });
    } else if (browser.dingding) {
      try {
        // 钉钉 同步更新 document.title，防止被自动覆盖成 index.html 的 title
        document.title = title;
        window.dd.biz.navigation.setTitle({ title });
      } catch (ex) {
        // 钉钉标题设置失败
      }
    } else {
      document.title = title;
    }
    setNavTitle(title);
  }
  /**
   * 设置标题栏可见性
   * @param show 是否显示
   */
  public setTitlebarVisible(show: boolean) {
    if (this.isApp) {
      this.action('setTitlebarVisible', { show });
    }
    setNavVisible(show);
    this.isNavbar = show;
  }
  /**
   * 关闭页面
   */
  public closePage() {
    if (this.isApp) {
      this.actionNoCb('goBack', true);
    } else if (browser.weixin) {
      if (browser.miniProgram) {
        // 微信小程序，关闭web-view
        window.wx.miniProgram.navigateBack();
      } else {
        window.wx.closeWindow();
        // ios 需要用下面的方式关闭
        document.addEventListener(
          'WeixinJSBridgeReady',
          () => {
            window.WeixinJSBridge.call('closeWindow');
          },
          false,
        );
      }
    } else if (browser.dingding) {
      window.dd.biz.navigation.close();
    } else if (browser.feishu) {
      window.h5sdk.biz.navigation.close({
        onSuccess: () => 1,
      });
    } else if (browser.cmiot) {
      window.CMIOT.navigateBack();
    } else if (browser.yadea) {
      // 雅迪App关闭webview
      if (browser.ios) {
        if (window.webkit) {
          window.webkit.messageHandlers.onClickLeftClose.postMessage('IOS点击关闭');
        } else {
          window.location.href = 'onClickLeftClose://a?b';
        }
      } else if (window.WebViewJavascriptBridge) {
        window.WebViewJavascriptBridge.onClickLeftClose();
      }
    } else if (window.lsym) {
      // 零食有鸣
      window.lsym.postMessage('back');
    } else if (browser.weaver) {
      // 泛微App
      window.em.closeWindow();
    } else {
      window.opener = null;
      window.close();
      // 通用关闭方法
      console.log('🚀 ~ closePage ~ 通用关闭方法:');
      if (window.wdzWebview) {
        console.log('🚀 ~ 存在 window.wdzWebview ');
        if (window.wdzWebview.closePage) {
          // 第三方 Android
          console.log('🚀 ~ 执行 window.wdzWebview.closePage');
          window.wdzWebview.closePage('{}');
        } else if (window.wdzWebview.postMessage) {
          // 第三方 flutter
          console.log('🚀 ~ 执行 window.wdzWebview.postMessage');
          window.wdzWebview.postMessage(JSON.stringify({ method: 'closePage' }));
        }
      } else if (window.webkit) {
        console.log('🚀 ~ 存在 window.webkit ');
        // 第三方 iOS
        window.webkit.messageHandlers.closePage?.postMessage('{}');
      }
    }
  }

  /**
   * 页面后退
   */
  public goback() {
    if (this.isApp) {
      this.action('goBackOrClose');
    } else {
      window.history.back();
    }
  }

  /**
   * 拦截页面后退
   * @param handler 回调方法，不传则是取消，回调参数canGoBack: webview是否可以后退
   */
  public beforeBack(handler?: (canGoBack: boolean) => void) {
    if (this.isApp) {
      const isIntercept = Boolean(handler);
      if (isIntercept) {
        this.win.beforeWebviewBack = (canGoBack: boolean) => {
          if (typeof handler === 'function') {
            handler(canGoBack);
          }
        };
      } else {
        delete this.win.beforeWebviewBack;
      }
      this.actionNoCb('interceptBack', isIntercept);
    }
  }

  /**
   * 设置语言
   * @param lang 显示语言
   */
  public setLanguage(lang: string) {
    setLanguage(lang);
    this.language = lang;
  }

  /**
   * 加载语言包
   * @param args 模块信息
   */
  private async loadLocale(args: Array<I18nModule>) {
    let enterpriseType = '1';
    try {
      const myInfo = await this.getMyInfo();
      enterpriseType = (myInfo.enterprise.enterpriseType || 1).toString();
    } catch (error) {
      enterpriseType = '1';
    }
    setEnterpriseType(enterpriseType);

    try {
      // 获取多语言集合，检测系统是否支持该语言，如果不支持则默认设置为 en-US
      const locale = (await http({
        method: 'get',
        url: `${this.sourceOrigin}/i18n/locale.json`,
      })) as any;
      if (!locale[this.language!]) {
        if (locale[this.fallbackLanguage]) {
          this.language = this.fallbackLanguage;
        } else {
          this.language = Language.ZH_CN;
        }
      }
      this.languageType = locale[this.language!].apiType;

      if (this.i18n && enterpriseType !== '1') {
        // 非门店企业，包装 i18n.t / i18n.tc，更新术语替换规则
        const termInfo = await http({
          method: 'get',
          url: `${this.sourceOrigin}/i18n/term-replace.json`,
        });
        wrapI18nTranslate(this.i18n);
        updateActiveReplaceRules(termInfo as any, this.language!);
      }

      const getLocale = (module: string, subModule?: string) => {
        return http({
          method: 'get',
          url: `${this.sourceOrigin}/i18n/h5/${module}/${subModule ? subModule + '/' : ''}${
            this.language
          }.json`,
        });
      };
      const revertFunc = (obj: any) => {
        for (const key in obj) {
          const value = obj[key];
          if (typeof value === 'object') {
            revertFunc(value);
          } else if (/(=>)|(function.*\(.*\{)/.test(value)) {
            obj[key] = new Function('return ' + value)();
          }
        }
      };

      const messages: LocaleObject = {};
      const requests = [
        getLocale(window.Vue?.version?.startsWith('3') ? 'vant' : 'vant2'), // 加载vant语言
        getLocale('common', 'default'), // 加载通用语言
      ];

      // 加载模块语言
      if (args.length) {
        if (typeof args[0] === 'string') {
          // 单模块
          args = [[args[0], args[1] as string]];
        }
        args.forEach((arg) => {
          if (!Array.isArray(arg)) return;
          // 获取单个模块的语言文件请求
          const [module, subModules] = arg;
          if (!module) return;

          // h5 暂时不需要加载 大模块 的默认语言包
          // requests.push(getLocale(module));
          if (subModules) {
            let subArr = null;
            if (typeof subModules === 'string') {
              subArr = [subModules];
            } else if (Array.isArray(subModules)) {
              subArr = subModules;
            }
            if (subArr) {
              subArr.forEach((subModule) => requests.push(getLocale(module, subModule)));
            }
          }
        });
      }
      const result = await Promise.all(requests);
      const vantLocale = result.shift() as LocaleObject;
      revertFunc(vantLocale);
      result.forEach((m) => {
        Object.assign(messages, m);
      });

      return [messages, vantLocale];
    } catch (error) {
      // 加载语言文件失败
      console.log('🚀 ~ Ovopark ~ loadLocale ~ error:', error);
      throw error;
    }
  }

  /**
   * 初始化token信息
   */
  private async initToken() {
    if (this.isApp) {
      // 万店掌App userAgent标识: ovopark/{version}/{token}/{uuid}/{language}
      const matches = navigator.userAgent.match(/ovopark\/(\S+)?(?:\s|$)/);
      if (matches?.length) {
        const arr = matches[1].split('/');
        this.uuid = arr[2];
        // 获取App版本，取版本前两段转换为数字 4.68.01 -> 468
        this.browser.appVersion = Number(arr[0].split('.').slice(0, 2).join(''));
        if (this.tokenMgmt.token === arr[1]) {
          // token没改变，cookie数据读到tokenMgmt对象中
          this.tokenMgmt.setData();
          return;
        }
      }
      try {
        // 调用协议获取token信息
        const res = await this.getAppToken();
        this.setToken(res as TokenProps);
      } catch (error) {
        // 获取AppToken失败
      }
    } else {
      await this.validateH5Token();
    }
  }

  /** 获取AppToken */
  public getAppToken(
    orgResolve?: (value: unknown) => void,
    orgReject?: (error: unknown) => void,
    count = 0,
  ) {
    // webview initialization end
    // webview注入js方法滞后，所以需要递归调用多次
    return new Promise((resolve, reject) => {
      if (!orgResolve) {
        orgResolve = resolve;
      }
      if (!orgReject) {
        orgReject = reject;
      }
      this.action<TokenProps>('getToken')
        .then((res) => {
          orgResolve!(res);
        })
        .catch(() => {
          count++;
          if (count > 100) {
            // 重试100次，1s后还是失败，就放弃
            console.log('getToken重试100次了，放弃');
            orgReject!({ message: 'getAppToken failed' });
            return;
          }
          console.log('获取AppToken失败，重试');
          setTimeout(() => {
            this.getAppToken(orgResolve, orgReject, count);
          }, 10);
        });
    });
  }

  /** 校验H5 token */
  private validateH5Token() {
    return new Promise((resolve) => {
      const result = this.tokenMgmt.setData();
      switch (result) {
        case -1:
          // token 不存在或已失效
          console.log('token失效');
          this.intercept = (type: EventType, handler: Handler) => {
            if (type === 'token-invalid') {
              this.intercept = undefined;
              handler && handler();
            }
          };
          resolve(false);
          break;
        case 0:
          // token正常，定时刷新
          resolve(true);
          break;
        case 1:
          // 等待刷新完成
          console.log('等待token刷新');
          // eslint-disable-next-line no-case-declarations
          const refreshComplete = (res: any) => {
            console.log('token刷新完成', res);
            resolve(true);
          };
          this.tokenMgmt.once('refreshed', refreshComplete);
          this.tokenMgmt.once('refreshError', refreshComplete);
          break;
      }
    });
  }

  /**
   * 获取token
   * @returns token
   */
  public getToken() {
    if (this.browser.mobile) {
      // 移动端直接返回内存中的token
      return this.tokenMgmt.token;
    } else {
      // 非移动端，返回cookie中的token
      return this.tokenMgmt.getCookieToken();
    }
  }

  /**
   * 设置token信息，更新cookie的值
   * @param props token信息
   */
  public setToken(props: TokenProps) {
    if (props) {
      if (props.token === 'null') {
        props.token = '';
      }
      if (!props.tokenExpirationTimestamp) {
        props.tokenExpirationTimestamp = 0;
      }
      if (!props.refreshExpirationTimestamp) {
        props.refreshExpirationTimestamp = 0;
      }
    }
    return this.tokenMgmt.setData(props);
  }

  /**
   * 添加水印到页面
   */
  public addWatermark = addWatermark;
  /**
   * 移除页面水印
   */
  public removeWatermark = removeWatermark;
  /**
   * 获取水印图片base64
   */
  public getWatermark = getWatermark;
  /**
   * 添加全局水印
   */
  public addGlobalWatermark = addGlobalWatermark;

  /**
   * 埋点上报
   */
  public buriedPointUpload = buriedPointUpload;

  /**
   * 跳转费用中心
   */
  public gotoCostCenter = gotoCostCenter;

  /**
   * 获取当前用户信息
   */
  public async getMyInfo() {
    if (!this.myInfo) {
      this.myInfo = await getMyInfo();
    }
    return this.myInfo;
  }
  /**
   * 设置当前用户信息
   * @param myInfo 用户信息
   */
  public setMyInfo(myInfo: any) {
    this.myInfo = myInfo;
  }

  /** 绑定token事件 */
  private bindTokenEvents() {
    this.tokenMgmt.on('refreshApp', () => {
      this.action<TokenProps>('refreshToken').then((res) => {
        this.setToken(res);
        this.emit('token-refreshed', res.token);
      });
    });
    this.tokenMgmt.on('refreshed', (token) => {
      this.emit('token-refreshed', token);
    });
    this.tokenMgmt.on('invalid', () => {
      if (this.isApp) {
        // App内不定时校验token了，老项目写的cookie有问题，会导致App被踢出
        // this.action<TokenProps>('invalidToken');
      } else {
        this.emit('token-invalid');
      }
    });
  }

  /** app主动调用方法 */
  private appCallMethods() {
    if (browser.isIframe) {
      // iframe页面不需要绑定方法
      return;
    }
    window.changeTheme = window.saveiOSHeights = () => {
      // 定义无用的 app 回调方法
    };
    // app主动刷新token
    window.setNewToken = (
      token: string,
      tokenExpirationTimestamp: number,
      refreshExpirationTimestamp: number,
    ) => {
      this.setToken({
        token,
        tokenExpirationTimestamp,
        refreshExpirationTimestamp,
      });
      this.emit('token-refreshed', token);
      window.document.querySelectorAll('iframe').forEach((iframe) => {
        try {
          iframe.contentWindow?.ovopark?.emit('token-refreshed', token);
        } catch (ex) {
          console.log('Notice iframe token-refreshed error.', iframe.src);
        }
      });
    };
  }
}
